// frontend/src/components/client/ClientAircraft.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plane, ChevronLeft, Gauge, Image as ImageIcon,
  FileText, RefreshCw, AlertTriangle, Clock,
  CheckCircle, XCircle, Fuel, Weight, Zap,
  MapPin, Hash, Calendar, Info
} from 'lucide-react';
import axios from 'axios';

// ── API helpers ──────────────────────────────────────────────────────────────
const clientApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8001/api',
});
clientApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('client_access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const getMyAircraftList  = ()         => clientApi.get('/client/aircraft/');
const getAircraftDetail  = (id)       => clientApi.get(`/client/aircraft/${id}`);
const getAircraftPhotos  = (id)       => clientApi.get(`/client/aircraft/${id}/photos`);
const getAircraftLicenses = (id)      => clientApi.get(`/client/aircraft/${id}/licenses`);
const getAircraftRenewals = (id)      => clientApi.get(`/client/aircraft/${id}/renewals`);

// ── Tiny helpers ─────────────────────────────────────────────────────────────
const fmt = (val, unit = '') =>
  val != null && val !== 0 ? `${val}${unit}` : '—';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

// ── Sub-components ───────────────────────────────────────────────────────────

const StatusPill = ({ status }) => {
  const map = {
    active:        { label: 'Active',        cls: 'bg-green-500/20 text-green-400 border-green-500/30' },
    operational:   { label: 'Operational',   cls: 'bg-green-500/20 text-green-400 border-green-500/30' },
    maintenance:   { label: 'Maintenance',   cls: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    grounded:      { label: 'Grounded',      cls: 'bg-red-500/20 text-red-400 border-red-500/30' },
    inactive:      { label: 'Inactive',      cls: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  };
  const cfg = map[status?.toLowerCase()] || map.active;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${cfg.cls}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
      {cfg.label.toUpperCase()}
    </span>
  );
};

const SpecCard = ({ icon: Icon, label, value, accent = 'blue' }) => {
  const accents = {
    blue:   'text-blue-400 bg-blue-500/10',
    cyan:   'text-cyan-400 bg-cyan-500/10',
    orange: 'text-orange-400 bg-orange-500/10',
    green:  'text-green-400 bg-green-500/10',
    purple: 'text-purple-400 bg-purple-500/10',
    red:    'text-red-400 bg-red-500/10',
  };
  return (
    <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4 flex flex-col gap-2">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accents[accent]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="text-lg font-bold text-white leading-none">{value || '—'}</p>
    </div>
  );
};

const SpecRow = ({ label, value }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-700/40 last:border-0">
    <span className="text-sm text-gray-400">{label}</span>
    <span className="text-sm text-white font-medium text-right max-w-[60%]">{value || '—'}</span>
  </div>
);

// ── TABS ─────────────────────────────────────────────────────────────────────

const TabSpecs = ({ aircraft }) => (
  <div className="space-y-6">
    {/* Quick stats */}
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
      <SpecCard icon={Gauge}  label="Seats"         value={fmt(aircraft.seats)}                          accent="blue"   />
      <SpecCard icon={Fuel}   label="Fuel Cap"       value={fmt(aircraft.fuel_capacity_gallons, ' gal')} accent="orange" />
      <SpecCard icon={Zap}    label="Engine Count"   value={fmt(aircraft.engine_count)}                  accent="cyan"   />
      <SpecCard icon={Weight} label="MTOW"           value={fmt(aircraft.max_takeoff_weight_lbs, ' lbs')} accent="purple" />
      <SpecCard icon={MapPin} label="Home Base"      value={aircraft.home_base}                          accent="green"  />
      <SpecCard icon={MapPin} label="Parking"        value={aircraft.parking_spot || aircraft.hangar_name} accent="red" />
    </div>

    {/* Full spec table */}
    <div className="bg-gray-800/40 border border-gray-700/50 rounded-xl p-5">
      <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
        Full Specifications
      </h4>
      <div>
        <SpecRow label="Registration"       value={aircraft.tail_number} />
        <SpecRow label="Manufacturer"       value={aircraft.manufacturer} />
        <SpecRow label="Model"              value={aircraft.model} />
        <SpecRow label="Year"               value={aircraft.year || aircraft.year_manufactured} />
        <SpecRow label="Aircraft Type"      value={aircraft.aircraft_type} />
        <SpecRow label="Serial Number"      value={aircraft.serial_number} />
        <SpecRow label="Callsign"           value={aircraft.callsign} />
        <SpecRow label="ICAO Code"          value={aircraft.icao_code} />
        <SpecRow label="Engine Type"        value={aircraft.engine_type} />
        <SpecRow label="Engine Make/Model"  value={[aircraft.engine_manufacturer, aircraft.engine_model].filter(Boolean).join(' ')} />
        <SpecRow label="Engine Count"       value={aircraft.engine_count} />
        <SpecRow label="Seats"              value={aircraft.seats} />
        <SpecRow label="Fuel Type"          value={aircraft.fuel_type} />
        <SpecRow label="Fuel Capacity"      value={fmt(aircraft.fuel_capacity_gallons, ' gal')} />
        <SpecRow label="Max Fuel"           value={fmt(aircraft.max_fuel_gallons, ' gal')} />
        <SpecRow label="MTOW"               value={fmt(aircraft.max_takeoff_weight_lbs, ' lbs')} />
        <SpecRow label="Max Landing Weight" value={fmt(aircraft.max_landing_weight_lbs, ' lbs')} />
        <SpecRow label="Airworthiness Date" value={fmtDate(aircraft.airworthiness_date)} />
        <SpecRow label="Certificate Issued" value={fmtDate(aircraft.certificate_issue_date)} />
        <SpecRow label="Hangar"             value={aircraft.hangar_name} />
        <SpecRow label="Hangar Location"    value={aircraft.hangar_location} />
      </div>
    </div>
  </div>
);

const TabPhotos = ({ photos, loading }) => {
  if (loading) return <LoadingSpinner />;
  if (!photos.length) return <EmptyState icon={ImageIcon} message="No photos uploaded yet" />;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {photos.map((p) => (
        <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer"
          className="relative group aspect-video rounded-xl overflow-hidden border border-gray-700 bg-gray-800">
          <img src={p.url} alt={p.caption || 'Aircraft photo'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          {p.caption && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-2">
              <p className="text-xs text-white truncate">{p.caption}</p>
            </div>
          )}
        </a>
      ))}
    </div>
  );
};

const LicenseStatusBadge = ({ status, expiryDate }) => {
  const days = daysUntil(expiryDate);
  if (days !== null && days < 0)
    return <span className="flex items-center gap-1 text-xs text-red-400"><XCircle className="w-3 h-3" />Expired</span>;
  if (days !== null && days < 30)
    return <span className="flex items-center gap-1 text-xs text-yellow-400"><AlertTriangle className="w-3 h-3" />Expires in {days}d</span>;
  return <span className="flex items-center gap-1 text-xs text-green-400"><CheckCircle className="w-3 h-3" />Valid</span>;
};

const TabLicenses = ({ licenses, loading }) => {
  if (loading) return <LoadingSpinner />;
  if (!licenses.length) return <EmptyState icon={FileText} message="No licenses on file" />;
  return (
    <div className="space-y-3">
      {licenses.map((lic) => (
        <div key={lic.id} className="bg-gray-800/50 border border-gray-700/60 rounded-xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold">{lic.license_type}</p>
              {lic.license_number && (
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <Hash className="w-3 h-3" />{lic.license_number}
                </p>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                {lic.issued_date && (
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />Issued: {fmtDate(lic.issued_date)}
                  </span>
                )}
                {lic.expiry_date && (
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Clock className="w-3 h-3" />Expires: {fmtDate(lic.expiry_date)}
                  </span>
                )}
              </div>
              {lic.notes && <p className="text-xs text-gray-500 mt-2">{lic.notes}</p>}
            </div>
            <LicenseStatusBadge status={lic.status} expiryDate={lic.expiry_date} />
          </div>
          {lic.document_url && (
            <a href={lic.document_url} target="_blank" rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition">
              <FileText className="w-3 h-3" />View document
            </a>
          )}
        </div>
      ))}
    </div>
  );
};

const RenewalUrgency = ({ dueDate }) => {
  const days = daysUntil(dueDate);
  if (days === null) return null;
  if (days < 0)   return <span className="text-xs font-semibold text-red-400">Overdue by {Math.abs(days)}d</span>;
  if (days < 30)  return <span className="text-xs font-semibold text-yellow-400">Due in {days}d</span>;
  if (days < 90)  return <span className="text-xs font-semibold text-orange-400">Due in {days}d</span>;
  return <span className="text-xs text-gray-500">Due in {days}d</span>;
};

const TabRenewals = ({ renewals, loading }) => {
  if (loading) return <LoadingSpinner />;
  if (!renewals.length) return <EmptyState icon={RefreshCw} message="No mandatory renewals scheduled" />;

  const sorted = [...renewals].sort((a, b) => new Date(a.due_date) - new Date(b.due_date));

  return (
    <div className="space-y-3">
      {sorted.map((r) => {
        const days = daysUntil(r.due_date);
        const isUrgent = days !== null && days < 30;
        const isOverdue = days !== null && days < 0;
        return (
          <div key={r.id}
            className={`border rounded-xl p-4 ${
              isOverdue ? 'bg-red-500/5 border-red-500/30' :
              isUrgent  ? 'bg-yellow-500/5 border-yellow-500/30' :
                          'bg-gray-800/50 border-gray-700/60'
            }`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold">{r.renewal_type}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />Due: {fmtDate(r.due_date)}
                  </span>
                  {r.completed_date && (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />Done: {fmtDate(r.completed_date)}
                    </span>
                  )}
                </div>
                {r.notes && <p className="text-xs text-gray-500 mt-2">{r.notes}</p>}
              </div>
              <RenewalUrgency dueDate={r.due_date} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Utility components ───────────────────────────────────────────────────────

const LoadingSpinner = () => (
  <div className="flex justify-center py-12">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
  </div>
);

const EmptyState = ({ icon: Icon, message }) => (
  <div className="text-center py-16 bg-gray-800/30 border border-dashed border-gray-700 rounded-xl">
    <Icon className="w-10 h-10 text-gray-600 mx-auto mb-3" />
    <p className="text-gray-500 text-sm">{message}</p>
  </div>
);

// ── Aircraft card (fleet list) ───────────────────────────────────────────────

const AircraftCard = ({ aircraft, onClick }) => (
  <button
    onClick={() => onClick(aircraft)}
    className="w-full text-left bg-gray-800/50 hover:bg-gray-800/80 active:bg-gray-700/60 border border-gray-700 hover:border-blue-500/50 rounded-xl p-5 transition-all duration-200 group"
  >
    <div className="flex items-start gap-4">
      <div className="p-3 bg-blue-500/10 rounded-xl group-hover:bg-blue-500/20 transition-colors">
        <Plane className="w-6 h-6 text-blue-400" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xl font-bold text-white tracking-wide">{aircraft.tail_number}</span>
          <StatusPill status={aircraft.status || 'active'} />
        </div>
        <p className="text-sm text-gray-400 mt-1">
          {[aircraft.manufacturer, aircraft.model, aircraft.year].filter(Boolean).join(' · ')}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3">
          {aircraft.aircraft_type && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Info className="w-3 h-3" />{aircraft.aircraft_type}
            </span>
          )}
          {aircraft.fuel_type && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Fuel className="w-3 h-3" />{aircraft.fuel_type}
            </span>
          )}
          {(aircraft.hangar_name || aircraft.parking_spot) && (
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" />{aircraft.hangar_name || aircraft.parking_spot}
            </span>
          )}
        </div>
      </div>
      <ChevronLeft className="w-5 h-5 text-gray-500 group-hover:text-blue-400 rotate-180 transition-colors flex-shrink-0 mt-1" />
    </div>
  </button>
);

// ── Main component ───────────────────────────────────────────────────────────

const TABS = [
  { id: 'specs',    label: 'Specs',    icon: Gauge },
  { id: 'photos',   label: 'Photos',   icon: ImageIcon },
  { id: 'licenses', label: 'Licenses', icon: FileText },
  { id: 'renewals', label: 'Renewals', icon: RefreshCw },
];

const ClientAircraft = () => {
  // Fleet list state
  const [fleet,          setFleet]          = useState([]);
  const [fleetLoading,   setFleetLoading]   = useState(true);

  // Selected aircraft state
  const [selected,       setSelected]       = useState(null);   // full detail object
  const [detailLoading,  setDetailLoading]  = useState(false);
  const [activeTab,      setActiveTab]      = useState('specs');

  // Tab data
  const [photos,         setPhotos]         = useState([]);
  const [licenses,       setLicenses]       = useState([]);
  const [renewals,       setRenewals]       = useState([]);
  const [photosLoading,  setPhotosLoading]  = useState(false);
  const [licensesLoading,setLicensesLoading]= useState(false);
  const [renewalsLoading,setRenewalsLoading]= useState(false);

  // ── Load fleet on mount ────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await getMyAircraftList();
        const list = res.data;
        setFleet(list);
        // Auto-select if only one aircraft
        if (list.length === 1) openDetail(list[0]);
      } catch (e) {
        console.error('Error loading aircraft list:', e);
      } finally {
        setFleetLoading(false);
      }
    })();
  }, []);

  // ── Open detail view ───────────────────────────────────────────────────────
  const openDetail = useCallback(async (aircraft) => {
    setDetailLoading(true);
    setSelected(null);
    setPhotos([]); setLicenses([]); setRenewals([]);
    setActiveTab('specs');

    try {
      const res = await getAircraftDetail(aircraft.id);
      setSelected(res.data);
    } catch (e) {
      console.error('Error loading aircraft detail:', e);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // ── Load tab data on tab change ────────────────────────────────────────────
  useEffect(() => {
    if (!selected) return;

    if (activeTab === 'photos' && !photos.length) {
      setPhotosLoading(true);
      getAircraftPhotos(selected.id)
        .then(r => setPhotos(r.data))
        .catch(() => {})
        .finally(() => setPhotosLoading(false));
    }

    if (activeTab === 'licenses' && !licenses.length) {
      setLicensesLoading(true);
      getAircraftLicenses(selected.id)
        .then(r => setLicenses(r.data))
        .catch(() => {})
        .finally(() => setLicensesLoading(false));
    }

    if (activeTab === 'renewals' && !renewals.length) {
      setRenewalsLoading(true);
      getAircraftRenewals(selected.id)
        .then(r => setRenewals(r.data))
        .catch(() => {})
        .finally(() => setRenewalsLoading(false));
    }
  }, [activeTab, selected]);

  const goBack = () => {
    if (fleet.length === 1) return; // single aircraft → no fleet list to go back to
    setSelected(null);
    setDetailLoading(false);
  };

  // ── Render: loading fleet ──────────────────────────────────────────────────
  if (fleetLoading) {
    return (
      <div className="px-4 sm:px-0">
        <h2 className="text-xl font-bold text-white mb-4">My Aircraft</h2>
        <LoadingSpinner />
      </div>
    );
  }

  // ── Render: empty fleet ────────────────────────────────────────────────────
  if (!fleet.length) {
    return (
      <div className="px-4 sm:px-0">
        <h2 className="text-xl font-bold text-white mb-2">My Aircraft</h2>
        <p className="text-gray-400 text-sm mb-6">Your registered aircraft</p>
        <EmptyState icon={Plane} message="No aircraft registered. Contact your FBO administrator." />
      </div>
    );
  }

  // ── Render: loading detail ─────────────────────────────────────────────────
  if (detailLoading) {
    return (
      <div className="px-4 sm:px-0">
        <h2 className="text-xl font-bold text-white mb-4">My Aircraft</h2>
        <LoadingSpinner />
      </div>
    );
  }

  // ── Render: fleet list ─────────────────────────────────────────────────────
  if (!selected) {
    return (
      <div className="px-4 sm:px-0">
        <div className="mb-5">
          <h2 className="text-xl font-bold text-white">My Aircraft</h2>
          <p className="text-gray-400 text-sm">{fleet.length} aircraft registered</p>
        </div>
        <div className="space-y-3">
          {fleet.map(a => (
            <AircraftCard key={a.id} aircraft={a} onClick={openDetail} />
          ))}
        </div>
      </div>
    );
  }

  // ── Render: aircraft detail ────────────────────────────────────────────────
  return (
    <div className="px-4 sm:px-0">
      {/* Back button — only shown when fleet has multiple aircraft */}
      {fleet.length > 1 && (
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition mb-5 group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to fleet
        </button>
      )}

      {/* Hero header */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-5 mb-5">
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 uppercase tracking-widest mb-1">
              {selected.manufacturer} // {selected.year || selected.year_manufactured}
            </p>
            <h2 className="text-3xl font-black text-white tracking-tight leading-none">
              {selected.tail_number}
            </h2>
            <p className="text-gray-400 mt-1">{selected.model}</p>
            <div className="mt-3">
              <StatusPill status={selected.status || 'active'} />
            </div>
          </div>

          {/* Quick stats grid */}
          <div className="grid grid-cols-2 gap-2 sm:w-56">
            {[
              { label: 'SEATS',    value: fmt(selected.seats) },
              { label: 'FUEL CAP', value: fmt(selected.fuel_capacity_gallons, 'g') },
              { label: 'MTOW',     value: fmt(selected.max_takeoff_weight_lbs, 'lb') },
              { label: 'FUEL TYPE',value: selected.fuel_type || '—' },
            ].map(({ label, value }) => (
              <div key={label} className="bg-gray-900/60 rounded-lg p-3">
                <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
                <p className="text-base font-bold text-white mt-0.5">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-800/40 border border-gray-700/50 rounded-xl p-1 mb-5 overflow-x-auto">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-1 justify-center ${
              activeTab === id
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'specs'    && <TabSpecs    aircraft={selected} />}
        {activeTab === 'photos'   && <TabPhotos   photos={photos}   loading={photosLoading} />}
        {activeTab === 'licenses' && <TabLicenses licenses={licenses} loading={licensesLoading} />}
        {activeTab === 'renewals' && <TabRenewals renewals={renewals} loading={renewalsLoading} />}
      </div>

      {/* Read-only notice */}
      <p className="text-center text-xs text-gray-600 mt-8">
        Aircraft information is managed by your FBO. Contact support for any changes.
      </p>
    </div>
  );
};

export default ClientAircraft;